import { GeoreferencedMap } from '@allmaps/annotation'
import { triangulateToUnique } from '@allmaps/triangulate'
import {
  mixNumbers,
  mixPoints,
  getPropertyFromDoubleCacheOrComputation,
  getPropertyFromQuintupleCacheOrComputation,
  mergeOptions,
  mixLineStrings,
  computeBbox,
  bboxToPolygon,
  combineBboxes,
  bufferBbox
} from '@allmaps/stdlib'

import { WarpedMap } from './WarpedMap.js'

import type {
  AnimationOptions,
  AnimationInternalOptions,
  SpecificTriangulatedWarpedMapOptions,
  TriangulatedWarpedMapOptions,
  WarpedMapListOptions,
  WarpedMapOptions,
  TriangulatedWarpedMapWithoutGeoreferencedMapOptions
} from '../shared/types.js'

import type {
  GcpAndDistortions,
  DistortionMeasure,
  TransformationType
} from '@allmaps/transform'
import type { Gcp, Point, Ring, TypedPolygon } from '@allmaps/types'
import type { TriangulationToUnique } from '@allmaps/triangulate'
import type { Projection } from '@allmaps/project'

export const DEFAULT_SPECIFIC_TRIANGULATED_WARPED_MAP_OPTIONS: SpecificTriangulatedWarpedMapOptions =
  {
    distortionMeasures: [] // List of distortion measures to compute starts empty, and is filled as individual distortion measures are requested. To precompute multiple distortion measures, pass a list here.
  }

export function createTriangulatedWarpedMapFactory() {
  return (
    mapId: string,
    georeferencedMap: GeoreferencedMap,
    listOptions?: Partial<WarpedMapListOptions<TriangulatedWarpedMap>>,
    mapOptions?: Partial<WarpedMapOptions>
  ) =>
    new TriangulatedWarpedMap(mapId, georeferencedMap, listOptions, mapOptions)
}

type GcpTriangulation = {
  resourceResolution: number | undefined
  gcpUniquePoints: GcpAndDistortions[]
  uniquePointIndices: number[]
  uniquePointIndexInterpolatedPolygon: TypedPolygon<number>
  uniquePointIndexInterpolatedSteinerPolygons: TypedPolygon<number>[]
  insideSteinerPolygonsTriangles: boolean[]
}

/**
 * Class for triangulated WarpedMaps.
 *
 * @param resourcePreviousResolution - Resolution (or 'distance') used during the previous triangulation of the resource mask
 * @param resourceResolution - Resolution (or 'distance') used during the triangulation of the resource mask, computed as the finest resource segment resulting from a resource-to-geo transformation of the horizontal and vertical midline of the resource bbox using the current transformation type
 * @param triangulateErrorCount - Number of time the triangulation has resulted in an error
 * @param projectedGcpPreviousTriangulation - Previous triangulation of the resource mask
 * @param projectedGcpTriangulation - Triangulation of the resource mask of a specific resource resolution, with unique points in resource and projected geospatial coordinates with distortions, and indices pointing to the triangles points and indices pointing to the triangulation-refined resource mask
 * @param resourceTrianglePoints - Triangle points of the triangles the triangulated resourceMask
 * @param projectedGeoPreviousTrianglePoints - The projectedGeoTrianglePoints of the previous transformation type, used during transformation transitions
 * @param projectedGeoTrianglePoints - The resourceTrianglePoints in projected geospatial coordinates
 * @param previousTrianglePointsDistortion - The trianglePointsDistortion of the previous transformation type, used during transformation transitions
 * @param trianglePointsDistortion - Distortion amount of the distortionMeasure at the projectedGeoTrianglePoints
 * @param projectedGeoPreviousTriangulationFullMask - The resource full mask refined by the previous triangulation, in projected geospatial coordinates
 * @param projectedGeoPreviousTriangulationMask - The resource mask refined by the previous triangulation, in projected geospatial coordinates
 * @param projectedGeoTriangulationMask - The resource mask refined by the triangulation, in projected geospatial coordinates
 * @param projectedGeoPreviousTriangulationAppliedMask - The resource applied mask refined by the previous triangulation, in projected geospatial coordinates
 * @param projectedGeoTriangulationAppliedMask - The resource applied mask refined by the triangulation, in projected geospatial coordinates
 */
export class TriangulatedWarpedMap extends WarpedMap {
  declare mapOptions: Partial<TriangulatedWarpedMapOptions>
  declare listOptions: Partial<TriangulatedWarpedMapOptions>
  declare georeferencedMapOptions: Partial<TriangulatedWarpedMapOptions>
  declare defaultOptions: TriangulatedWarpedMapOptions
  declare options: TriangulatedWarpedMapOptions

  previousResourceResolution: number | undefined
  resourceResolution: number | undefined

  triangulateErrorCount = 0

  projectedGcpPreviousTriangulation?: GcpTriangulation
  projectedGcpTriangulation?: GcpTriangulation
  protected resourceTriangulationCache: Map<
    number,
    Map<string, TriangulationToUnique>
  >
  protected projectedGcpTriangulationCache: Map<
    number,
    Map<
      string,
      Map<TransformationType, Map<string, Map<string, GcpTriangulation>>>
    >
  >

  resourceTrianglePoints: Point[] = []

  projectedGeoPreviousTrianglePoints: Point[] = []
  projectedGeoTrianglePoints: Point[] = []

  previousTrianglePointsDistortion: number[] = []
  trianglePointsDistortion: number[] = []

  previousTrianglePointsInside: boolean[] = []
  trianglePointsInside: boolean[] = []

  projectedGeoPreviousTriangulationFullMask: Ring = []
  projectedGeoTriangulationFullMask: Ring = []

  projectedGeoPreviousTriangulationMask: Ring = []
  projectedGeoTriangulationMask: Ring = []

  projectedGeoPreviousTriangulationAppliedMask: Ring = []
  projectedGeoTriangulationAppliedMask: Ring = []

  /**
   * Creates an instance of a TriangulatedWarpedMap.
   *
   * @param mapId - ID of the map
   * @param georeferencedMap - Georeferenced map used to construct the WarpedMap
   * @param options - Options
   */
  constructor(
    mapId: string,
    georeferencedMap: GeoreferencedMap,
    listOptions?: Partial<WarpedMapListOptions<TriangulatedWarpedMap>>,
    mapOptions?: Partial<WarpedMapOptions>
  ) {
    super(
      mapId,
      georeferencedMap,
      listOptions as Partial<WarpedMapListOptions<WarpedMap>>,
      mapOptions
    )

    this.resourceTriangulationCache = new Map()
    this.projectedGcpTriangulationCache = new Map()

    this.updateTriangulation()
  }

  /**
   * Get default options
   */
  static getDefaultOptions(): TriangulatedWarpedMapOptions {
    return mergeOptions(
      DEFAULT_SPECIFIC_TRIANGULATED_WARPED_MAP_OPTIONS,
      super.getDefaultOptions()
    )
  }

  /**
   * Get default options without the options overwritten by the georeferenced map
   */
  static getDefaultWithoutGeoreferencedMapOptions(): TriangulatedWarpedMapWithoutGeoreferencedMapOptions {
    return mergeOptions(
      DEFAULT_SPECIFIC_TRIANGULATED_WARPED_MAP_OPTIONS,
      super.getDefaultWithoutGeoreferencedMapOptions()
    )
  }

  /**
   * Set the defaultOptions
   */
  setDefaultOptions() {
    this.defaultOptions = TriangulatedWarpedMap.getDefaultOptions()
  }

  /**
   * Set the list options
   *
   * @param listOptions - list options
   * @param animationOptions - Animation options
   */
  setListOptions(
    listOptions?: Partial<TriangulatedWarpedMapOptions>,
    animationOptions?: Partial<AnimationOptions>
  ): object {
    return super.setListOptions(listOptions, animationOptions)
  }

  /**
   * Set the map-specific options
   *
   * @param mapOptions - Map-specific options
   * @param animationOptions - Animation options
   */
  setMapOptions(
    mapOptions?: Partial<TriangulatedWarpedMapOptions>,
    animationOptions?: Partial<AnimationOptions & AnimationInternalOptions>
  ): object {
    return super.setMapOptions(mapOptions, animationOptions)
  }

  /**
   * Set the map-specific options, and the list options
   *
   * @param mapOptions - Map-specific options
   * @param listOptions - list options
   * @param animationOptions - Animation options
   */
  setMapAndListOptions(
    mapOptions?: Partial<TriangulatedWarpedMapOptions>,
    listOptions?: Partial<TriangulatedWarpedMapOptions>,
    animationOptions?: Partial<AnimationOptions & AnimationInternalOptions>
  ): object {
    return super.setMapAndListOptions(mapOptions, listOptions, animationOptions)
  }

  /**
   * Update the ground control points loaded from a georeferenced map to new ground control points.
   *
   * @param gcps - the new ground control points
   */
  protected setGcps(gcps: Gcp[]) {
    super.setGcps(gcps)
    this.clearResourceTriangulationCaches()
    this.updateTriangulation()
  }

  /**
   * Update the resource mask loaded from a georeferenced map to a new mask.
   *
   * @param resourceMask - the new mask
   */
  protected setResourceMask() {
    super.setResourceMask()
    this.updateTriangulation()
  }

  /**
   * Set the distortionMeasure
   *
   * @param distortionMeasure - the disortion measure
   */
  protected setDistortionMeasure(distortionMeasure?: DistortionMeasure) {
    super.setDistortionMeasure(distortionMeasure)
    if (
      distortionMeasure &&
      !this.options.distortionMeasures.includes(distortionMeasure)
    ) {
      this.options.distortionMeasures.push(distortionMeasure)
    }
    this.updateTriangulation()
  }

  /**
   * Set the internal projection
   *
   * @param projection - the internal projection
   */
  protected setInternalProjection(projection: Projection) {
    super.setInternalProjection(projection)
    this.updateTriangulation()
  }

  /**
   * Set the projection
   *
   * @param projection - the projection
   */
  protected setProjection(projection: Projection) {
    super.setProjection(projection)
    this.clearProjectedTriangulationCaches()
    this.updateTriangulation()
  }

  /**
   * Reset previous transform properties to new ones (when completing an animation).
   */
  resetPrevious() {
    super.resetPrevious()
    this.previousResourceResolution = this.resourceResolution
    this.projectedGcpPreviousTriangulation = this.projectedGcpTriangulation
    this.projectedGeoPreviousTrianglePoints = this.projectedGeoTrianglePoints
    this.previousTrianglePointsDistortion = this.trianglePointsDistortion
    this.previousTrianglePointsInside = this.trianglePointsInside

    this.projectedGeoPreviousTriangulationFullMask =
      this.projectedGeoTriangulationFullMask
    this.projectedGeoPreviousTriangulationMask =
      this.projectedGeoTriangulationMask
    this.projectedGeoPreviousTriangulationAppliedMask =
      this.projectedGeoTriangulationAppliedMask
  }

  /**
   * Mix previous transform properties with new ones (when changing an ongoing animation).
   *
   * @param t - animation progress
   */
  mixPreviousAndNew(t: number) {
    super.mixPreviousAndNew(t)

    if (
      this.projectedGcpPreviousTriangulation &&
      this.projectedGcpTriangulation
    ) {
      const projectedGcpPreviousTriangulation =
        this.projectedGcpPreviousTriangulation
      const projectedGcpTriangulation = this.projectedGcpTriangulation

      this.previousResourceResolution = this.resourceResolution
      this.projectedGcpPreviousTriangulation = {
        resourceResolution:
          projectedGcpPreviousTriangulation.resourceResolution,
        gcpUniquePoints: projectedGcpPreviousTriangulation.gcpUniquePoints.map(
          (projectedGcp, index) => {
            return {
              resource: projectedGcp.resource,
              geo: mixPoints(
                projectedGcpTriangulation.gcpUniquePoints[index].geo,
                projectedGcp.geo,
                t
              ),
              // Note: Not mixing the distortions Map, only the active distortion
              distortions:
                projectedGcpTriangulation.gcpUniquePoints[index].distortions,
              distortion: mixNumbers(
                projectedGcpTriangulation.gcpUniquePoints[index].distortion ||
                  0,
                projectedGcp.distortion || 0,
                t
              )
            }
          }
        ),
        uniquePointIndices:
          projectedGcpPreviousTriangulation.uniquePointIndices,
        uniquePointIndexInterpolatedPolygon:
          projectedGcpPreviousTriangulation.uniquePointIndexInterpolatedPolygon,
        uniquePointIndexInterpolatedSteinerPolygons:
          projectedGcpPreviousTriangulation.uniquePointIndexInterpolatedSteinerPolygons,
        insideSteinerPolygonsTriangles:
          projectedGcpPreviousTriangulation.insideSteinerPolygonsTriangles
      }

      this.projectedGeoPreviousTrianglePoints =
        projectedGcpPreviousTriangulation.uniquePointIndices.map(
          (i) => projectedGcpPreviousTriangulation.gcpUniquePoints[i].geo
        )
      this.previousTrianglePointsDistortion =
        projectedGcpPreviousTriangulation.uniquePointIndices.map(
          (i) =>
            projectedGcpPreviousTriangulation.gcpUniquePoints[i]
              .distortion as number
        )
    }

    // Also mix mask
    this.projectedGeoPreviousTriangulationFullMask = mixLineStrings(
      this.projectedGeoTriangulationFullMask,
      this.projectedGeoPreviousTriangulationFullMask,
      t
    )
    this.projectedGeoPreviousTriangulationMask = mixLineStrings(
      this.projectedGeoTriangulationMask,
      this.projectedGeoPreviousTriangulationMask,
      t
    )
    this.projectedGeoPreviousTriangulationAppliedMask = mixLineStrings(
      this.projectedGeoTriangulationAppliedMask,
      this.projectedGeoPreviousTriangulationAppliedMask,
      t
    )
  }

  /**
   * Update the (previous and new) triangulation of the resourceMask. Use cache if available.
   */
  private updateTriangulation() {
    // The following ensures this function is only run after initialisation
    // This class' constructor calls this function twice
    // Once via super() and updateTransformerProperties()
    // but then the cache is not ready yet, so we make it return
    // And once at the end
    if (
      !this.resourceTriangulationCache ||
      !this.projectedGcpTriangulationCache
    ) {
      return
    }

    // Get resolution from transform
    const resourceResolution =
      this.options.resourceResolution ||
      this.projectedTransformer.getToGeoTransformationResolution(
        this.resourceMaskBbox
      )

    // Adapt resolution from previous
    let refinePrevious = false
    if (resourceResolution && this.previousResourceResolution) {
      refinePrevious = this.previousResourceResolution < resourceResolution
      this.resourceResolution = Math.min(
        resourceResolution,
        this.previousResourceResolution
      )
    }
    if (resourceResolution && !this.previousResourceResolution) {
      refinePrevious = true
      this.resourceResolution = resourceResolution
    }
    if (!resourceResolution && this.previousResourceResolution) {
      this.resourceResolution = this.previousResourceResolution
    } else if (!resourceResolution && !this.previousResourceResolution) {
      this.resourceResolution = undefined
    }

    // Compute triangulation
    this.projectedGcpTriangulation = getPropertyFromQuintupleCacheOrComputation(
      this.projectedGcpTriangulationCache,
      this.resourceResolution,
      String(this.resourceMask),
      this.transformationType,
      this.internalProjection.definition,
      String(this.options.distortionMeasures),
      () => {
        const {
          uniquePoints,
          uniquePointIndexTriangles,
          uniquePointIndexInterpolatedPolygon,
          uniquePointIndexInterpolatedSteinerPolygons,
          insideSteinerPolygonsTriangles
        } = getPropertyFromDoubleCacheOrComputation(
          this.resourceTriangulationCache,
          this.resourceResolution,
          String(this.resourceMask),
          () => {
            try {
              return triangulateToUnique(
                bboxToPolygon(
                  combineBboxes(
                    computeBbox(this.resourceFullMask),
                    bufferBbox(computeBbox(this.resourceMask), 1)
                    // Buffer mask before combining bboxes
                    // such that triangulated polygon (full mask + mask)
                    // and steiner polygons (mask)
                    // dont have touching edges, which hinders constraining triangulation
                    // This could make trianguation way too big if mask >> full mask
                    // In that case, consider clipping applyable mask.
                  )!
                ),
                this.resourceResolution,
                {
                  steinerPoints: this.gcps.map((gcp) => gcp.resource),
                  steinerPolygons: [[this.resourceMask]],
                  computeInsideSteinerPolygons: true
                }
              )
            } catch (error) {
              if (error instanceof Error) {
                error.message =
                  `Failed to triangulate map ${this.mapId}: ` + error.message
                throw error
              } else {
                throw new Error(`Failed to triangulate map ${this.mapId}`)
              }
            }
          }
        )

        // Extend Triangulation to ProjectedGcpTriangulation
        // By including projectedGeo and distortions
        const resourceResolution = this.resourceResolution
        const resourceUniquePoints = uniquePoints as Point[]
        const gcpUniquePoints =
          this.projectedTransformer.transformToProjectedGeo(
            resourceUniquePoints,
            {
              distortionMeasures: this.options.distortionMeasures,
              referenceScale: this.getReferenceScale(),
              isMultiGeometry: true
            },
            (gcpPartialDistortion) => gcpPartialDistortion
          )
        const uniquePointIndices = uniquePointIndexTriangles.flat() as number[]

        return {
          resourceResolution,
          gcpUniquePoints,
          uniquePointIndices,
          uniquePointIndexInterpolatedPolygon,
          uniquePointIndexInterpolatedSteinerPolygons,
          insideSteinerPolygonsTriangles
        }
      }
    )

    if (!this.projectedGcpPreviousTriangulation) {
      this.projectedGcpPreviousTriangulation = this.projectedGcpTriangulation
    }

    // If new triangulation has higher resolution then previous, refine previous from new
    if (refinePrevious) {
      this.previousResourceResolution = this.resourceResolution
      this.projectedGcpPreviousTriangulation =
        getPropertyFromQuintupleCacheOrComputation(
          this.projectedGcpTriangulationCache,
          this.previousResourceResolution,
          String(this.resourceMask),
          this.previousTransformationType,
          this.previousInternalProjection.definition,
          String(this.options.distortionMeasures),
          () => {
            if (!this.projectedGcpTriangulation) {
              // TODO: rewrite this function, make more readble?
              throw new Error('No projectedGcpTriangulation')
            }
            const projectedGcpTriangulation = this.projectedGcpTriangulation

            return {
              resourceResolution:
                this.projectedGcpTriangulation.resourceResolution,
              gcpUniquePoints:
                this.projectedPreviousTransformer.transformToProjectedGeo(
                  this.projectedGcpTriangulation.gcpUniquePoints.map(
                    (projectedGcp) => projectedGcp.resource
                  ),
                  {
                    distortionMeasures: this.options.distortionMeasures,
                    referenceScale: this.getReferenceScale(),
                    isMultiGeometry: true
                  },
                  (gcpPartialDistortion) => gcpPartialDistortion
                ),
              uniquePointIndices:
                this.projectedGcpTriangulation.uniquePointIndices,
              uniquePointIndexInterpolatedPolygon:
                projectedGcpTriangulation.uniquePointIndexInterpolatedPolygon,
              uniquePointIndexInterpolatedSteinerPolygons:
                projectedGcpTriangulation.uniquePointIndexInterpolatedSteinerPolygons,
              insideSteinerPolygonsTriangles:
                projectedGcpTriangulation.insideSteinerPolygonsTriangles
            }
          },
          () => !this.mixed,
          () => !this.mixed
        )
    }

    this.updateTrianglePoints()
  }

  /**
   * Derive the (previous and new) resource and projectedGeo points from their corresponding triangulations.
   *
   * Also derive the (previous and new) triangulation-refined resource and projectedGeo mask
   */
  private updateTrianglePoints() {
    if (
      !this.projectedGcpPreviousTriangulation ||
      !this.projectedGcpTriangulation
    ) {
      return
    }

    const projectedGcpPreviousTriangulation =
      this.projectedGcpPreviousTriangulation
    const projectedGcpTriangulation = this.projectedGcpTriangulation

    this.trianglePointsInside =
      projectedGcpTriangulation.insideSteinerPolygonsTriangles
        .map((inside) => [inside, inside, inside])
        .flat()

    this.resourceTrianglePoints =
      this.projectedGcpTriangulation.uniquePointIndices.map(
        (i) => projectedGcpTriangulation.gcpUniquePoints[i].resource
      )
    this.projectedGeoPreviousTrianglePoints =
      this.projectedGcpPreviousTriangulation.uniquePointIndices.map(
        (i) => projectedGcpPreviousTriangulation.gcpUniquePoints[i].geo
      )
    this.projectedGeoTrianglePoints =
      this.projectedGcpTriangulation.uniquePointIndices.map(
        (i) => projectedGcpTriangulation.gcpUniquePoints[i].geo
      )

    // Also update the mask

    // The triangulation produces triangles based on the full resource mask,
    // and refines that full mask in it's process (using the resource resolution),
    // giving a slighly different result then if we would simply transform
    // the full resource mask to projected geo space (because the transformation refinement
    // is different then the triangulation refinement).
    // To prevent noticable slivers between both,
    // we compute a triangulation-refined full mask in projected geo space
    // from the full resource mask as refined by/during the triangulation.
    // We do the same for the resource mask (and resource mask).
    // This way we obtain a mask that exactly matches the triangulation.
    this.projectedGeoPreviousTriangulationFullMask =
      this.projectedGcpPreviousTriangulation.uniquePointIndexInterpolatedPolygon
        .map((typedRing) =>
          typedRing.map(
            (i) => projectedGcpPreviousTriangulation.gcpUniquePoints[i].geo
          )
        )
        .flat()
    this.projectedGeoTriangulationFullMask =
      this.projectedGcpTriangulation.uniquePointIndexInterpolatedPolygon
        .map((typedRing) =>
          typedRing.map((i) => projectedGcpTriangulation.gcpUniquePoints[i].geo)
        )
        .flat()
    this.projectedGeoPreviousTriangulationMask =
      this.projectedGcpPreviousTriangulation.uniquePointIndexInterpolatedSteinerPolygons[0]
        .map((typedRing) =>
          typedRing.map(
            (i) => projectedGcpPreviousTriangulation.gcpUniquePoints[i].geo
          )
        )
        .flat()
    this.projectedGeoTriangulationMask =
      this.projectedGcpTriangulation.uniquePointIndexInterpolatedSteinerPolygons[0]
        .map((typedRing) =>
          typedRing.map((i) => projectedGcpTriangulation.gcpUniquePoints[i].geo)
        )
        .flat()
    // For the applied resource mask, this can be derived
    this.projectedGeoPreviousTriangulationAppliedMask = this.applyMask
      ? this.projectedGeoPreviousTriangulationMask
      : this.projectedGeoPreviousTriangulationFullMask
    this.projectedGeoTriangulationAppliedMask = this.applyMask
      ? this.projectedGeoTriangulationMask
      : this.projectedGeoTriangulationFullMask

    this.updateTrianglePointsDistortion()
  }

  /**
   * Derive the (previous and new) distortions from their corresponding triangulations.
   */
  private updateTrianglePointsDistortion() {
    if (
      !this.projectedGcpPreviousTriangulation ||
      !this.projectedGcpTriangulation
    ) {
      return
    }
    const projectedGcpPreviousTriangulation =
      this.projectedGcpPreviousTriangulation
    const projectedGcpTriangulation = this.projectedGcpTriangulation

    this.previousTrianglePointsDistortion =
      projectedGcpPreviousTriangulation.uniquePointIndices.map((i) => {
        const distortions =
          projectedGcpPreviousTriangulation.gcpUniquePoints[i].distortions
        if (!this.previousDistortionMeasure || !distortions) {
          return 0
        } else {
          return distortions.get(this.previousDistortionMeasure) as number
        }
      })
    this.trianglePointsDistortion =
      projectedGcpTriangulation.uniquePointIndices.map((i) => {
        const distortions =
          projectedGcpTriangulation.gcpUniquePoints[i].distortions
        if (!this.distortionMeasure || !distortions) {
          return 0
        } else {
          return distortions.get(this.distortionMeasure) as number
        }
      })
  }

  protected updateProjectedTransformerProperties() {
    super.updateProjectedTransformerProperties()
    this.updateTriangulation()
  }

  protected clearProjectedTransformerCaches() {
    super.clearProjectedTransformerCaches()
    this.clearResourceTriangulationCaches()
  }

  protected clearResourceTriangulationCaches() {
    this.resourceTriangulationCache = new Map()
    this.clearProjectedTriangulationCaches()
  }

  protected clearProjectedTriangulationCaches() {
    this.projectedGcpTriangulationCache = new Map()
  }
}
